import React, { FC, useContext, useEffect, useState, useMemo } from 'react'
import { 
  Button, 
  Table, 
  Input, 
  Tag, 
  Switch, 
  Form, 
  Modal, 
  notification,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Space,
  Typography
} from 'antd'
import { 
  PlusOutlined, 
  ExclamationCircleOutlined, 
  SearchOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  StopOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import Section from '../../components/Layout/Section'
import MainButton from '../../components/MainButton/MainButton'
import CountryIcon from '../../components/CountryIcon'
import ApiContext from '../../context/ApiContext'
import CountryContext from '../../context/CountryContext'
import { Country, CreateCountryDto, UpdateCountryDto } from '../../domain/country.type'
import { ColumnsType } from 'antd/lib/table/interface'
import TableHelper from '../../utils/TableHelper'
import FormatHelper from '../../utils/FormatHelper'
import styles from './CountryManagement.module.scss'

const { confirm } = Modal
const { Title, Text } = Typography

const CountryManagement: FC = () => {
  const { t } = useTranslation('countries')
  const { t: tCommon } = useTranslation('common')
  const { getAllCountries, createCountry, updateCountry, deleteCountry, updateCountryStatus } = useContext(ApiContext)
  const { refreshCountries } = useContext(CountryContext)
  
  const [countries, setCountries] = useState<Country[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false)
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [editingCountry, setEditingCountry] = useState<Country | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()

  const loadCountries = async () => {
    setLoading(true)
    try {
      const data = await getAllCountries()
      setCountries(data)
    } catch (error) {
      notification.error({
        message: tCommon('error'),
        description: t('notifications.loadError'),
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCountries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Computed statistics
  const statistics = useMemo(() => {
    const active = countries.filter(c => c.active).length
    const inactive = countries.filter(c => !c.active).length
    return {
      total: countries.length,
      active,
      inactive
    }
  }, [countries])

  // Filtered countries
  const filteredCountries = useMemo(() => {
    return countries.filter(country => {
      const matchesSearch = 
        country.code.toLowerCase().includes(searchText.toLowerCase()) ||
        country.name.toLowerCase().includes(searchText.toLowerCase())
      
      const matchesStatus = 
        statusFilter === 'all' ||
        (statusFilter === 'active' && country.active) ||
        (statusFilter === 'inactive' && !country.active)
      
      return matchesSearch && matchesStatus
    })
  }, [countries, searchText, statusFilter])

  const handleToggleActive = (country: Country) => {
    const newStatus = !country.active
    const titleKey = newStatus ? 'modal.confirm.activateTitle' : 'modal.confirm.deactivateTitle'
    const contentKey = newStatus ? 'modal.confirm.activateContent' : 'modal.confirm.deactivateContent'

    confirm({
      title: t(titleKey),
      icon: <ExclamationCircleOutlined />,
      content: t(contentKey, { name: country.name }),
      okText: t('modal.confirm.okText'),
      cancelText: t('modal.confirm.cancelText'),
      onOk: async () => {
        try {
          await updateCountryStatus(country.id, {
            active: newStatus,
            version: country.version!,
          })
          const successKey = newStatus ? 'notifications.activateSuccess' : 'notifications.deactivateSuccess'
          notification.success({
            message: tCommon('success'),
            description: t(successKey, { name: country.name }),
          })
          await Promise.all([loadCountries(), refreshCountries()])
        } catch (error: any) {
          if (error?.response?.status === 409) {
            if (error.response.data?.error === 'VERSION_MISMATCH') {
              notification.error({
                message: tCommon('conflict'),
                description: t('notifications.versionMismatch'),
              })
              loadCountries()
            } else if (error.response.data?.error === 'COUNTRY_IN_USE') {
              notification.error({
                message: tCommon('error'),
                description: t('notifications.countryInUse'),
              })
            }
          } else {
            notification.error({
              message: tCommon('error'),
              description: t('notifications.updateError'),
            })
          }
        }
      },
    })
  }

  const handleCreateCountry = async (values: CreateCountryDto) => {
    try {
      await createCountry({
        code: values.code.toUpperCase(),
        name: values.name,
        // Flag path is optional - backend will auto-generate if not provided
        flagIconPath: values.flagIconPath && values.flagIconPath.trim() !== '' 
          ? values.flagIconPath 
          : undefined,
      })
      notification.success({
        message: tCommon('success'),
        description: t('notifications.createSuccess', { name: values.name }),
      })
      setCreateModalVisible(false)
      form.resetFields()
      await Promise.all([loadCountries(), refreshCountries()])
    } catch (error: any) {
      if (error?.response?.status === 409) {
        notification.error({
          message: tCommon('error'),
          description: t('notifications.codeExists', { code: values.code }),
        })
      } else {
        notification.error({
          message: tCommon('error'),
          description: t('notifications.createError'),
        })
      }
    }
  }

  const handleEditClick = (country: Country) => {
    setEditingCountry(country)
    setEditModalVisible(true)
    // Delay form population until modal is open to prevent warnings
    setTimeout(() => {
      editForm.setFieldsValue({
        name: country.name,
        flagIconPath: country.flagIconPath,
      })
    }, 0)
  }

  const handleEditCountry = async (values: { name: string; flagIconPath?: string }) => {
    if (!editingCountry) return

    try {
      const updateDto: UpdateCountryDto = {
        name: values.name,
        flagIconPath: values.flagIconPath,
        version: editingCountry.version!,
      }
      
      await updateCountry(editingCountry.id, updateDto)
      
      notification.success({
        message: tCommon('success'),
        description: t('notifications.updateSuccess', { name: values.name }),
      })
      setEditModalVisible(false)
      setEditingCountry(null)
      editForm.resetFields()
      await Promise.all([loadCountries(), refreshCountries()])
    } catch (error: any) {
      if (error?.response?.status === 409) {
        notification.error({
          message: tCommon('conflict'),
          description: t('notifications.versionMismatch'),
        })
        loadCountries()
      } else {
        notification.error({
          message: tCommon('error'),
          description: t('notifications.updateError'),
        })
      }
    }
  }

  const handleDeleteClick = (country: Country) => {
    confirm({
      title: t('modal.confirm.deleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('modal.confirm.deleteContent', { name: country.name }),
      okText: t('modal.confirm.okText'),
      okType: 'danger',
      cancelText: t('modal.confirm.cancelText'),
      onOk: async () => {
        try {
          await deleteCountry(country.id, country.version!)
          notification.success({
            message: tCommon('success'),
            description: t('notifications.deleteSuccess', { name: country.name }),
          })
          await Promise.all([loadCountries(), refreshCountries()])
        } catch (error: any) {
          if (error?.response?.status === 409) {
            if (error.response.data?.error === 'COUNTRY_IN_USE') {
              notification.error({
                message: tCommon('error'),
                description: t('notifications.countryInUse'),
              })
            } else {
              notification.error({
                message: tCommon('conflict'),
                description: t('notifications.versionMismatch'),
              })
              loadCountries()
            }
          } else {
            notification.error({
              message: tCommon('error'),
              description: t('notifications.deleteError'),
            })
          }
        }
      },
    })
  }

  const formatDate = (date: string | undefined | null) => {
    if (!date || date === 'null' || date === '') return '-'
    try {
      const timestamp = new Date(date).getTime()
      if (isNaN(timestamp)) return '-'
      return FormatHelper.formatDateTime(timestamp)
    } catch (error) {
      console.warn('Invalid date format:', date)
      return '-'
    }
  }

  const formatUser = (email: string | undefined | null) => {
    if (!email) return '-'
    if (email === 'SYSTEM') return t('system') || 'Système'
    return FormatHelper.formatEmail(email)
  }

  const formatUserWithTooltip = (email: string | undefined | null) => {
    if (!email) return '-'
    if (email === 'SYSTEM') return t('system') || 'Système'
    return email
  }

  const columnConfig: ColumnsType<Country> = [
    {
      title: t('table.code'),
      key: 'code',
      dataIndex: 'code',
      sorter: TableHelper.stringCompare('code'),
      width: 100,
      render: (code: string) => (
        <Space>
          <CountryIcon country={code} size={24} />
          <strong>{code}</strong>
        </Space>
      ),
    },
    {
      title: t('table.name'),
      key: 'name',
      dataIndex: 'name',
      sorter: TableHelper.stringCompare('name'),
      width: 200,
    },
    {
      title: t('table.status'),
      key: 'active',
      dataIndex: 'active',
      width: 120,
      filters: [
        { text: t('status.active'), value: true },
        { text: t('status.inactive'), value: false },
      ],
      onFilter: (value, record) => record.active === value,
      render: (active: boolean) => (
        <Tag 
          icon={active ? <CheckCircleOutlined /> : <StopOutlined />}
          color={active ? 'success' : 'default'}
        >
          {active ? t('status.active') : t('status.inactive')}
        </Tag>
      ),
    },
    {
      title: t('table.createdBy'),
      key: 'createdBy',
      dataIndex: 'createdBy',
      width: 180,
      render: (email: string | null) => {
        const displayText = formatUser(email)
        const tooltipText = formatUserWithTooltip(email)
        return (
          <Tooltip title={tooltipText}>
            <Text ellipsis style={{ color: email === 'SYSTEM' ? '#1890ff' : undefined }}>
              {displayText}
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('table.createdAt'),
      key: 'createdAt',
      dataIndex: 'createdAt',
      width: 180,
      sorter: TableHelper.dateCompare('createdAt'),
      render: (date: string | null) => {
        const formattedDate = formatDate(date)
        return (
          <Tooltip title={date || t('table.noData')}>
            <Text type={date ? undefined : 'secondary'}>
              {formattedDate}
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('table.updatedAt'),
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      width: 180,
      sorter: TableHelper.dateCompare('updatedAt'),
      render: (date: string | null) => {
        const formattedDate = formatDate(date)
        return (
          <Tooltip title={date || t('table.noData')}>
            <Text type={date ? undefined : 'secondary'}>
              {formattedDate}
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('table.deactivatedBy'),
      key: 'deactivatedBy',
      dataIndex: 'deactivatedBy',
      width: 180,
      render: (email: string | undefined) => {
        const displayText = formatUser(email)
        const tooltipText = formatUserWithTooltip(email)
        return (
          <Tooltip title={tooltipText}>
            <Text 
              type={email && email !== '-' ? 'warning' : 'secondary'} 
              ellipsis
              style={{ color: email === 'SYSTEM' ? '#1890ff' : undefined }}
            >
              {displayText}
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('table.deactivatedAt'),
      key: 'deactivatedAt',
      dataIndex: 'deactivatedAt',
      width: 180,
      sorter: TableHelper.dateCompare('deactivatedAt'),
      render: (date: string | undefined) => {
        const formattedDate = formatDate(date)
        return (
          <Tooltip title={date || t('table.noData')}>
            <Text type={date ? 'warning' : 'secondary'}>
              {formattedDate}
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('table.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, country) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(country)}
            size="small"
          >
            {t('table.edit')}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(country)}
            size="small"
          >
            {t('table.delete')}
          </Button>
          <Switch
            checked={country.active}
            onChange={() => handleToggleActive(country)}
            checkedChildren={t('status.active')}
            unCheckedChildren={t('status.inactive')}
          />
        </Space>
      ),
    },
  ]

  return (
    <>
      <Section>
        <div className={styles.titleSection}>
          <Title level={2}>{t('title')}</Title>
          <MainButton onClick={() => setCreateModalVisible(true)} size="large">
            <PlusOutlined /> {t('addCountry')}
          </MainButton>
        </div>
        
        {/* Statistics Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={t('statistics.total')}
                value={statistics.total}
                prefix={<GlobalOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={t('statistics.active')}
                value={statistics.active}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={t('statistics.inactive')}
                value={statistics.inactive}
                prefix={<StopOutlined />}
                valueStyle={{ color: '#8c8c8c' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Search and Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} md={16}>
            <Input
              placeholder={t('search')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={8}>
            <Space.Compact block>
              <Button
                type={statusFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('all')}
                style={{ flex: 1 }}
              >
                {t('filters.all')}
              </Button>
              <Button
                type={statusFilter === 'active' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('active')}
                style={{ flex: 1 }}
              >
                {t('filters.active')}
              </Button>
              <Button
                type={statusFilter === 'inactive' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('inactive')}
                style={{ flex: 1 }}
              >
                {t('filters.inactive')}
              </Button>
            </Space.Compact>
          </Col>
        </Row>

        {/* Table */}
        <Table
          rowKey={(country: Country) => country.id}
          columns={columnConfig}
          dataSource={filteredCountries}
          loading={loading}
          scroll={{ x: 1400 }}
          locale={{
            emptyText: t('table.noData'),
            triggerDesc: tCommon('triggerDesc'),
            triggerAsc: tCommon('triggerAsc'),
            cancelSort: tCommon('cancelSort'),
          }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `${total} ${t('statistics.total').toLowerCase()}`,
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Section>

      {/* Create Modal */}
      <Modal
        title={t('modal.create.title')}
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        okText={t('modal.create.okText')}
        cancelText={t('modal.create.cancelText')}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateCountry}
        >
          <Form.Item
            name="code"
            label={t('modal.create.codeLabel')}
            rules={[
              { required: true, message: t('modal.create.codeRequired') },
              { 
                pattern: /^[A-Z]{2}$/, 
                message: t('modal.create.codePattern')
              },
            ]}
          >
            <Input
              placeholder={t('modal.create.codePlaceholder')}
              maxLength={2}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('modal.create.nameLabel')}
            rules={[{ required: true, message: t('modal.create.nameRequired') }]}
          >
            <Input placeholder={t('modal.create.namePlaceholder')} />
          </Form.Item>
          {/* Hidden: Icon path field - backend auto-generates from country code
          <Form.Item
            name="flagIconPath"
            label={t('modal.create.flagLabel')}
          >
            <Input placeholder={t('modal.create.flagPlaceholder')} />
          </Form.Item>
          */}
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={t('modal.edit.title')}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          setEditingCountry(null)
          editForm.resetFields()
        }}
        onOk={() => editForm.submit()}
        okText={t('modal.edit.okText')}
        cancelText={t('modal.edit.cancelText')}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditCountry}
        >
          <Form.Item label={t('modal.edit.codeLabel')}>
            <Input value={editingCountry?.code} disabled />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('modal.edit.nameLabel')}
            rules={[{ required: true, message: t('modal.edit.nameRequired') }]}
          >
            <Input placeholder={t('modal.edit.namePlaceholder')} />
          </Form.Item>
          {/* Hidden: Icon path field - backend auto-generates from country code
          <Form.Item
            name="flagIconPath"
            label={t('modal.edit.flagLabel')}
          >
            <Input placeholder={t('modal.edit.flagPlaceholder')} />
          </Form.Item>
          */}
        </Form>
      </Modal>
    </>
  )
}

export default CountryManagement
